import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Chip,
  Avatar,
} from '@mui/material'
import {
  Dashboard,
  ShoppingCart,
  People,
  AccountBalance,
} from '@mui/icons-material'
import { useAppSelector } from '../../hooks/redux'
import logoImage from '../../assets/images/logo.png'
import { colors } from '../../theme/colors'

interface NavigationItem {
  id: string
  label: string
  path: string
  icon: React.ReactElement
  badge?: string | number
  roles?: string[]
}

const navigationItems: NavigationItem[] = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    path: '/dashboard',
    icon: <Dashboard />,
  },
  {
    id: 'orders',
    label: 'Orders',
    path: '/orders',
    icon: <ShoppingCart />,
  },
  {
    id: 'employees',
    label: 'Employees',
    path: '/employees',
    icon: <People />,
  },
  {
    id: 'payroll',
    label: 'Payroll',
    path: '/payroll',
    icon: <AccountBalance />,
    roles: ['admin', 'hr'],
  },
]

export const Sidebar: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user } = useAppSelector((state) => state.auth)
  const { isConnected } = useAppSelector((state) => state.realtime)

  const handleNavigation = (path: string) => {
    navigate(path)
  }

  const isActive = (path: string) => {
    return location.pathname.startsWith(path)
  }

  const hasAccess = (item: NavigationItem) => {
    if (!item.roles || !user?.role) return true
    return item.roles.includes(user.role.toLowerCase())
  }

  const filteredItems = navigationItems.filter(hasAccess)

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      backgroundColor: colors.sidebar.background,
      color: colors.textOnDark.primary,
    }}>
      {/* Dark Sidebar Header */}
      <Box sx={{ p: 3, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: 'linear-gradient(135deg, #2196f3 0%, #1565c0 100%)',
              width: 40,
              height: 40,
            }}
          >
            <img
              src={logoImage}
              alt="GF"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
              }}
            />
          </Avatar>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ color: 'white' }}>
              GF System
            </Typography>
            <Chip
              label={isConnected ? 'Online' : 'Offline'}
              size="small"
              sx={{
                fontSize: '0.7rem',
                height: 20,
                backgroundColor: isConnected ? 'rgba(76, 175, 80, 0.2)' : 'rgba(244, 67, 54, 0.2)',
                color: isConnected ? '#81c784' : '#e57373',
                fontWeight: 500,
                border: `1px solid ${isConnected ? '#81c784' : '#e57373'}`,
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* Dark Sidebar Navigation */}
      <List sx={{ flexGrow: 1, px: 1, py: 2 }}>
        {filteredItems.map((item) => (
          <ListItem key={item.id} disablePadding sx={{ mb: 0.5 }}>
            <ListItemButton
              selected={isActive(item.path)}
              onClick={() => handleNavigation(item.path)}
              sx={{
                borderRadius: 2,
                mx: 1,
                color: 'white',
                transition: 'all 0.2s ease',
                ...(isActive(item.path) && {
                  bgcolor: 'rgba(33, 150, 243, 0.15)',
                  borderLeft: '3px solid #2196f3',
                  '&:hover': {
                    bgcolor: 'rgba(33, 150, 243, 0.25)',
                  },
                }),
                ...(!isActive(item.path) && {
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)',
                  },
                }),
              }}
            >
              <ListItemIcon
                sx={{
                  color: isActive(item.path) ? '#2196f3' : 'rgba(255, 255, 255, 0.7)',
                  minWidth: 40,
                }}
              >
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontWeight: isActive(item.path) ? 600 : 400,
                  fontSize: '0.875rem',
                }}
              />
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  sx={{
                    ml: 1,
                    fontSize: '0.7rem',
                    height: 20,
                    backgroundColor: '#f44336',
                    color: 'white',
                    fontWeight: 600,
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* User Profile Section */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: 'rgba(255, 255, 255, 0.05)',
            cursor: 'pointer',
            transition: 'background-color 0.2s ease',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: '#2196f3',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {user?.full_name?.charAt(0) || 'U'}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600} sx={{ color: 'white' }}>
              {user?.full_name || 'User'}
            </Typography>
            <Typography variant="caption" sx={{ color: colors.textOnDark.secondary }}>
              {user?.role || 'Role'}
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
